// Page planning for the PDF export. All measurements are CSS pixels measured
// from the top of the captured element.

export interface PdfBlock {
  bottom: number;
  top: number;
}

export interface PdfPage {
  end: number;
  start: number;
}

// The gap between two dashboard rows. A new page starts at the next block's top
// when it is within this distance of the break, so pages don't open on a blank
// strip; further away than that the break was inside a block and continues there.
const MAX_SKIPPED_GAP_PX = 48;

// The dashboard's rows are the children of the first element that has more
// than one child — the page root sits inside single-child wrappers (the scroll
// container, the outlet). Each child is a "block" that should stay on one page.
export const collectBlocks = (root: HTMLElement): PdfBlock[] => {
  let container: Element = root;
  while (container.children.length === 1) {
    [container] = Array.from(container.children);
  }
  const rootTop = root.getBoundingClientRect().top;
  return [...container.children]
    .map((child) => {
      const { bottom, top } = child.getBoundingClientRect();
      return { bottom: bottom - rootTop, top: top - rootTop };
    })
    .filter(({ bottom, top }) => bottom - top > 0)
    .sort((blockA, blockB) => blockA.top - blockB.top);
};

interface PlanPagesInput {
  blocks: PdfBlock[];
  // Where to break inside a block that is taller than a page: given the
  // nominal break and the earliest allowed one, returns the break to use.
  breakInsideBlock: (nominal: number, earliest: number) => number;
  pageHeight: number;
  totalHeight: number;
}

// Breaks pages between blocks: each page takes as many whole blocks as fit,
// and the next page starts at the first block that didn't. Only a block taller
// than a page is split, using `breakInsideBlock`.
export const planPages = ({
  blocks,
  breakInsideBlock,
  pageHeight,
  totalHeight,
}: PlanPagesInput): PdfPage[] => {
  const pages: PdfPage[] = [];
  let start = 0;

  while (totalHeight - start > pageHeight) {
    // Fixed for this page so the callbacks below don't close over a variable
    // that the loop reassigns.
    const pageStart = start;
    const limit = pageStart + pageHeight;
    const fitting = blocks.filter(
      ({ bottom }) => bottom > pageStart && bottom <= limit
    );
    const lastFitting = fitting[fitting.length - 1];
    // A break at the bottom of a block that fits; otherwise (one block fills
    // the whole page) split inside it.
    const end = lastFitting
      ? lastFitting.bottom
      : Math.max(pageStart + 1, breakInsideBlock(limit, pageStart));
    pages.push({ end, start: pageStart });

    const next = blocks.find(({ top }) => top >= end);
    start = next && next.top - end <= MAX_SKIPPED_GAP_PX ? next.top : end;
  }

  pages.push({ end: totalHeight, start });
  return pages;
};
